import { DragResizeModal } from "../ContextMenu/Modals/DragResizeModal";
import debug from "debug";
const log = debug("app:image");

/**
 * Handles the creation and interaction of the Title Block Modal UI.
 * @category Components
 * @subcategory TitleBlock
 */
export class TitleBlockModal {
  /**
   * Constructs a new TitleBlockModal instance.
   * @param {Object} experience - The shared application resources object.
   */
  constructor(experience) {
    this.modal = null;
    this.button = null;
    this.experience = experience;
    this.dragResizeModal = null; // Initialize dragResizeModal
    this.bomData = this.experience.bomData; // Keeping bomData reference
    this.powerTableData = this.experience.powerTableData; // Keeping powerTableData reference
    this.savedData = {}; // Store the saved data here
    this.eventHub = this.experience.eventHub; // Initialize eventHub for emitting events

  }


  cleanup() {
    if (!this.modal) return; // Prevent unnecessary operations

    log("Cleaning up TitleBlockModal...");

    // Remove event listeners
    this.modal.querySelector("#download")?.removeEventListener("click", this.handleDownloadUI);
    this.modal.querySelector("#closeModal")?.removeEventListener("click", this.hideModal);
    
    // Remove inputs to prevent memory leaks
    Object.keys(this.inputs).forEach((key) => {
      this.inputs[key].removeEventListener("input", this.populateInputsWithSavedData);
      this.inputs[key] = null;
    });

    // Remove modal from DOM
    this.modal.remove();
    this.modal = null;

    // Clear references
    this.inputs = {};
    this.savedData = {};
    this.dragResizeModal = null;
    
    // Unregister from event hub
    this.eventHub.off("modal:downloadRequested", this.handleDownloadUI);
    this.eventHub.off("titleblock", this.openTitleModal);

    log("TitleBlockModal cleanup completed.");
  }

  // Removed init() since it was unused and redundant


  /**
   * Opens the title block modal and populates it with saved data.
   * @param {Object} sizes - The dimensions of the modal container.
   * @param {number} sizes.width - The width of the container.
   * @param {number} sizes.height - The height of the container.
   * @param {number} sizes.top - The top offset of the container.
   */
  openTitleModal(sizes) {
    if (this.modal) {
      this.eventHub.emit("lockEvents", true);
      this.populateInputsWithSavedData();
      this.showModal();
      return;
    }

    this.createModal(sizes);
    this.populateInputsWithSavedData();
    this.showModal();
  }

  /**
   * Creates the modal UI with the provided sizes and appends it to the DOM.
   * @param {Object} sizes - The dimensions of the modal container.
   */
  createModal(sizes) {
    log("sizes", sizes, sizes.top);
    const modalWidth = 520;
    const topPosition = sizes.top + sizes.height / 2 - 200;
    const leftPosition = sizes.width / 2 - modalWidth / 2;

    this.modal = document.createElement("div");
    this.modal.style.position = "fixed";
    this.modal.style.top = `${topPosition}px`;
    this.modal.style.left = `${leftPosition}px`;
    this.modal.style.transform = "translate(0, 0)";
    this.modal.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
    this.modal.style.borderRadius = "10px";
    this.modal.style.width = `${modalWidth}px`;
    this.modal.style.padding = "20px";
    this.modal.style.boxSizing = "border-box";
    this.modal.style.backgroundColor = "white";
    this.modal.style.zIndex = "101";
    this.modal.style.pointerEvents = "auto";

    // Modal content setup
    this.modal.innerHTML = `
      <div class="titleblock-modal-header">
        <h3>Edit Titleblock</h3>
      </div>
      <div class="titleblock-modal-content" style="width: 100%; max-width: 100%; box-sizing: border-box; display: grid; row-gap: 5px;">
        
        <!-- Company -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="company" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Company</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="company" maxlength="36" style="grid-column: 3 / span 3; width: 100%; height: 20px; margin-bottom: 0;" required>
        </div>

        <!-- Title -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="title" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Title</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="title" maxlength="36" style="grid-column: 3 / span 3; width: 100%; height: 20px; margin-bottom: 0;" required>
        </div>

        <!-- Drawing No. and Revision -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="drawing-no" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Drawing No.</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="drawing-no" maxlength="6" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <label for="revision" style="grid-column: 4; text-align: left; font-size: 12px; color: #888; margin-bottom: 0; padding-left: 10px;">Revision</label>
          <input type="text" id="revision" maxlength="6" style="grid-column: 5; width: 100%; height: 20px; margin-bottom: 0;" required>
        </div>

        <!-- Drawn By, Date -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="drawn-by" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Drawn By</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="drawn-by" maxlength="36" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <input type="date" id="drawn-by-date" style="grid-column: 5; width: 100%; height: 20px;" required>
        </div>

        <!-- Checked By, Date -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="checked-by" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Checked By</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="checked-by" maxlength="6" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <input type="date" id="checked-by-date" style="grid-column: 5; width: 100%; height: 20px;" required>
        </div>

        <!-- Engineering Approval, Date -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="eng-approval" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Eng. Appr.</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="eng-approval" maxlength="6" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <input type="date" id="eng-approval-date" style="grid-column: 5; width: 100%; height: 20px;" required>
        </div>

        <!-- Manufacturing Approval, Date -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="mfg-approval" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Mfg. Appr.</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="mfg-approval" maxlength="6" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <input type="date" id="mfg-approval-date" style="grid-column: 5; width: 100%; height: 20px;" required>
        </div>

        <!-- QA, Date -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="qa" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">QA</label>
          <div style="grid-column: 2;"></div>
          <input type="text" id="qa" maxlength="6" style="grid-column: 3; width: 100%; height: 20px; margin-bottom: 0;" required>
          <input type="date" id="qa-date" style="grid-column: 5; width: 100%; height: 20px;" required>
        </div>

        <!-- Note -->
        <div class="input-row" style="display: grid; grid-template-columns: 20% 5% 30% 15% 30%; align-items: center;">
          <label for="note" style="grid-column: 1; text-align: left; font-size: 12px; color: #888; margin-bottom: 0;">Note</label>
          <div style="grid-column: 2;"></div>
          <textarea id="note" rows="3" maxlength="100" style="grid-column: 3 / span 3; width: 100%; resize: none; margin-bottom: 0;" placeholder="Note"></textarea>
        </div>
      </div>

      <div style="margin-top: 10px; display: flex; justify-content: space-between; align-items: center;">
        <div style="flex-grow: 1; font-size: 12px; display: flex; align-items: center;">
          <button id="download" class="download-pdf-btn">Generate</button>
          <span id="generatingMessage" style="display: none; margin-left: 10px;">PDF is generating...</span>
          <span id="dataSavedMessage" style="display: none; margin-left: 10px;">Data saved</span>
          <span id="downloadLink" style="display: none; margin-left: 10px;">
            <a id="downloadLinkUrl">Download the PDF</a>
          </span>
        </div>
        <div style="flex-shrink: 0;">
          <button id="cancel" style="margin-right: 10px;" class="cancel-titleblock">Close</button>
          <button id="ok" class="okbtn-titleblock">Save</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);

    const header = this.modal.querySelector(".titleblock-modal-header");
    // Initialize drag-resize functionality
    this.dragResizeModal = new DragResizeModal(header, this.modal);

    document.getElementById("cancel").addEventListener("click", () => {
      log("Cancel button clicked");
      this.resetModalState();
      this.hideModal();
      this.experience.eventHub.emit("titleblock");
    });

    document.getElementById("download").addEventListener("click", () => {
      log("Download button clicked");
      this.handleDownloadUI();
      this.experience.eventHub.emit("modal:downloadRequested"); // Emit event for download
    });

    document.getElementById("ok").addEventListener("click", () => {
      this.saveTitleblockModal();
    });
  }

  addDynamicUpdateListeners() {
    if (this.savedData) {
      document.getElementById("companyGrid").innerText = this.savedData.company || "";
      document.getElementById("titleGrid").innerText = this.savedData.title || "";
      document.getElementById("rev").innerText = this.savedData.rev || "";
      document.getElementById("dwgNo").innerText = this.savedData.dwgno || "";
      document.getElementById("drawnName").innerText = this.savedData.drawnby || "";
      document.getElementById("drawnDate").innerText = this.savedData.dbdate || "";
      document.getElementById("checkedName").innerText = this.savedData.checkedby || "";
      document.getElementById("checkedDate").innerText = this.savedData.cbdate || "";
      document.getElementById("engApprName").innerText = this.savedData.engappr || "";
      document.getElementById("engApprDate").innerText = this.savedData.eadate || "";
      document.getElementById("mfgApprName").innerText = this.savedData.mfgappr || "";
      document.getElementById("mfgApprDate").innerText = this.savedData.madate || "";
      document.getElementById("qaName").innerText = this.savedData.qa || "";
      document.getElementById("qaDate").innerText = this.savedData.qadate || "";
      document.getElementById("noteGrid").innerText = this.savedData.note || "";
    }

    const fieldMap = {
      "drawn-by": "drawnName",
      "drawn-by-date": "drawnDate",
      "checked-by": "checkedName",
      "checked-by-date": "checkedDate",
      "eng-approval": "engApprName",
      "eng-approval-date": "engApprDate",
      "mfg-approval": "mfgApprName",
      "mfg-approval-date": "mfgApprDate",
      "qa": "qaName",
      "qa-date": "qaDate",
      "note": "noteGrid",
      "company": "companyGrid",
      "title": "titleGrid",
      "drawing-no": "dwgNo",
      "revision": "rev"
    };
  
    Object.keys(fieldMap).forEach((fieldId) => {
      const inputField = document.getElementById(fieldId);
      const valueElementId = fieldMap[fieldId];
  
      if (inputField) {
        inputField.addEventListener("input", () => {
          const valueElement = document.getElementById(valueElementId);
          if (valueElement) {
            valueElement.innerText = inputField.value;
          }
        });
      }
    });
  }

  /**
   * Updates the download button UI and shows a generating message.
   */
  handleDownloadUI() {
    const downloadButton = document.getElementById("download");
    const generatingMessage = document.getElementById("generatingMessage");

    if (downloadButton && generatingMessage) {
      // Disable the download button and show the generating message
      downloadButton.disabled = true;
      downloadButton.style.backgroundColor = "#808080";
      downloadButton.style.cursor = "default";
      generatingMessage.style.display = "inline"; // Show the "PDF is generating..." message
    }
  }

  /**
   * Shows the modal by setting its display style to "block".
   */
  showModal() {
    if (this.modal) {
      this.modal.style.display = "block";
    }
  }

  /**
   * Hides the modal by setting its display style to "none" and unlocks events.
   */
  hideModal() {
    if (this.modal) {
      this.eventHub.emit("lockEvents", false);
      this.modal.style.display = "none";
    }
  }

  /**
   * Resets the modal's input fields and saved messages.
   */
  resetModalState() {
    const inputFields = document.querySelectorAll(
      ".titleblock-modal-content input"
    );
    inputFields.forEach((input) => {
      input.value = "";
    });

    this.resetMessages();
  }

  /**
   * Resets messages shown in the modal, such as download links or saved messages.
   */
  resetMessages() {
    const downloadLink = document.getElementById("downloadLink");
    if (downloadLink) {
      downloadLink.style.display = "none";
    }
  }

  /**
   * Retrieves the values from the modal inputs.
   * @returns {Object} An object containing the modal's input values.
   */
  getTitleBlockModal() {
    return {
      company: document.getElementById("company").value,
      title: document.getElementById("title").value,
      rev: document.getElementById("revision").value,
      dwgno: document.getElementById("drawing-no").value,
      drawnby: document.getElementById("drawn-by").value,
      dbdate: document.getElementById("drawn-by-date").value,
      checkedby: document.getElementById("checked-by").value,
      cbdate: document.getElementById("checked-by-date").value,
      engappr: document.getElementById("eng-approval").value,
      eadate: document.getElementById("eng-approval-date").value,
      mfgappr: document.getElementById("mfg-approval").value,
      madate: document.getElementById("mfg-approval-date").value,
      qa: document.getElementById("qa").value,
      qadate: document.getElementById("qa-date").value,
      note: document.getElementById("note").value,

    };
  }

  /**
   * Saves the current modal data into the savedData property.
   * @param {boolean} [showMessage=true] - Whether to show a "data saved" message.
   */
  saveTitleblockModal(showMessage = true) {
    this.savedData = this.getTitleBlockModal();
    log(this.savedData, "Saved Input Data");

    const dataSavedMessage = document.getElementById("dataSavedMessage");
    const downloadLink = document.getElementById("downloadLink");

    if (downloadLink) {
      downloadLink.style.display = "none";
    }

    if (showMessage && dataSavedMessage) {
      dataSavedMessage.style.display = "inline";
      setTimeout(() => {
        dataSavedMessage.style.display = "none";
      }, 2000);
    }
  }


  // Method for showing the generated download link

  /**
   * Displays the generated PDF download link in the modal.
   * @param {string} downloadUrl - The URL of the generated PDF.
   */
  showDownloadLink(downloadUrl) {
    const downloadLinkUrl = document.getElementById("downloadLinkUrl");
    const downloadLink = document.getElementById("downloadLink");

    if (downloadLinkUrl && downloadLink) {
      downloadLinkUrl.href = downloadUrl;
      downloadLinkUrl.download = "generated.pdf";
      downloadLink.style.display = "block";

      // Reset the button and hide the "generating" message
      const downloadButton = document.getElementById("download");
      const generatingMessage = document.getElementById("generatingMessage");

      if (downloadButton && generatingMessage) {
        downloadButton.disabled = false;
        downloadButton.style.backgroundColor = "#004976";
        downloadButton.style.cursor = "pointer";
        generatingMessage.style.display = "none";
      }
    }
  }

  /**
   * Populates the modal inputs with saved data.
   */
  populateInputsWithSavedData() {
    log("this.savedData", this.savedData);
    if (this.savedData) {
      document.getElementById("company").value = this.savedData.company || "";
      document.getElementById("companyGrid").value = this.savedData.company || "";
      document.getElementById("title").value = this.savedData.title || "";
      document.getElementById("revision").value = this.savedData.rev || "";
      document.getElementById("drawing-no").value = this.savedData.dwgno || "";
      document.getElementById("drawn-by").value = this.savedData.drawnby || "";
      document.getElementById("drawn-by-date").value = this.savedData.dbdate || "";
      document.getElementById("checked-by").value = this.savedData.checkedby || "";
      document.getElementById("checked-by-date").value = this.savedData.cbdate || "";
      document.getElementById("eng-approval").value = this.savedData.engappr || "";
      document.getElementById("eng-approval-date").value = this.savedData.eadate || "";
      document.getElementById("mfg-approval").value = this.savedData.mfgappr || "";
      document.getElementById("mfg-approval-date").value = this.savedData.madate || "";
      document.getElementById("qa").value = this.savedData.qa || "";
      document.getElementById("qa-date").value = this.savedData.qadate || "";
      document.getElementById("note").value = this.savedData.note || "";
      this.addDynamicUpdateListeners();
    }
  }
}
